// src/theme/theme.create.js
import { createTheme } from '@mui/material/styles';
import { lightPalette } from './palettes/lightPalette';
import { darkPalette } from './palettes/darkPalette';
import { spacing, spacingFunction, controlHeights, density } from './tokens/spacing';
import { radius, radiusPx } from './tokens/radius';
import { shadows } from './tokens/shadows';
import { typography, typographyVars, typographyStyles } from './tokens/typography';
import { derivePrimaryScale, hexToRgba, sanitizeThemeConfig } from './utils/colorUtils';
import { buildModeAwareSemantic } from './utils/modeAwareSemantic';
import { CHAT_PRIMARY } from './tokens/chatMessage';
import { BRAND_PRIMARY, FEATURE_PRIMARY_FOREGROUND } from './tokens/semantic';
import {
  getFormControlThemeOverrides,
  getFormHelperTextThemeOverrides,
  getFormLabelThemeOverrides,
} from '../components/ui/Form/controls/formControlStyles';

export const layout = {
  sidebarWidthExpanded: '184px',
  sidebarWidthCollapsed: '64px',
  headerHeight: '72px',
  contentMaxWidth: '1600px',
};

export function resolveThemeMode(themeConfig, systemMode = 'light') {
  const requested = themeConfig?.mode || 'system';
  if (requested === 'system') return systemMode;
  return requested === 'dark' ? 'dark' : 'light';
}

export function resolveDensityConfig(themeConfig) {
  const currentDensity =
    themeConfig?.density && density[themeConfig.density] ? themeConfig.density : 'default';
  const densityConfig = density[currentDensity];
  return {
    current: currentDensity,
    controlHeights: {
      sm: densityConfig.controlHeight - 6,
      md: densityConfig.controlHeight,
      lg: densityConfig.controlHeight + 4,
    },
    cardPadding: densityConfig.cardPadding,
    rowHeight: densityConfig.rowHeight,
    spacingScale: densityConfig.spacing,
  };
}

/**
 * Shared theme resolution — brand (global) and chat (Intervene) scales stay independent.
 */
function resolveThemeContext(themeConfig = {}, systemMode = 'light') {
  const sanitized = sanitizeThemeConfig(themeConfig);
  const requestedMode = resolveThemeMode(sanitized, systemMode);
  const basePalette = requestedMode === 'dark' ? darkPalette : lightPalette;
  const customPalette = { ...basePalette };

  if (sanitized.primary_color) {
    const primaryScale = derivePrimaryScale(sanitized.primary_color, requestedMode);
    customPalette.primary = {
      ...(customPalette.primary || {}),
      main: primaryScale.main,
      light: primaryScale.light,
      dark: primaryScale.dark,
      contrastText: primaryScale.contrastText,
    };
  }

  if (sanitized.secondary_color) {
    const secondaryScale = derivePrimaryScale(sanitized.secondary_color, requestedMode);
    customPalette.secondary = {
      ...(customPalette.secondary || {}),
      main: secondaryScale.main,
      light: secondaryScale.light,
      dark: secondaryScale.dark,
      contrastText: secondaryScale.contrastText,
    };
  }

  if (sanitized.background_color) {
    customPalette.background = {
      ...(customPalette.background || {}),
      default: sanitized.background_color,
    };
  }

  const brandMain =
    sanitized.primary_color || customPalette.primary?.main || lightPalette.primary.main || BRAND_PRIMARY;
  const chatMain = sanitized.chat_primary_color || CHAT_PRIMARY;
  const brandScale = derivePrimaryScale(brandMain, requestedMode);
  const chatScale = derivePrimaryScale(chatMain, requestedMode);
  const densityResolved = resolveDensityConfig(sanitized);
  const resolvedSemantic = buildModeAwareSemantic({
    mode: requestedMode,
    customPalette,
    brandMain,
    brandScale,
    chatMain,
    chatScale,
  });

  return {
    sanitized,
    requestedMode,
    customPalette,
    brandScale,
    chatScale,
    densityResolved,
    resolvedSemantic,
  };
}

function buildCustomTokens({ densityResolved, resolvedSemantic, requestedMode }) {
  return {
    radius,
    radiusPx,
    shadows,
    spacing,
    controlHeights: densityResolved.controlHeights,
    density: {
      ...density,
      current: densityResolved.current,
      cardPadding: densityResolved.cardPadding,
      rowHeight: densityResolved.rowHeight,
      spacingScale: densityResolved.spacingScale,
    },
    semantic: resolvedSemantic,
    typography,
    typographyVars,
    typographyStyles,
    layout,
    mode: requestedMode,
  };
}

/**
 * Feature pages use chat green as the visible primary (Intervene, License, Onboarding).
 * Brand blue stays in buildDynamicTheme for eventual global rollout.
 */
function applyFeatureChatAccent({ customPalette, chatScale, resolvedSemantic }) {
  const featurePalette = {
    ...customPalette,
    primary: {
      ...(customPalette.primary || {}),
      main: chatScale.main,
      light: chatScale.light,
      dark: chatScale.dark,
      contrastText: FEATURE_PRIMARY_FOREGROUND,
    },
  };

  const featureSemantic = {
    ...resolvedSemantic,
    chat: {
      ...resolvedSemantic.chat,
      primaryContrast: FEATURE_PRIMARY_FOREGROUND,
      outboundText: FEATURE_PRIMARY_FOREGROUND,
    },
    interactive: {
      ...resolvedSemantic.interactive,
      primary: chatScale.main,
      primaryHover: chatScale.hover,
      primaryActive: chatScale.active,
      primarySoft: chatScale.soft,
      primaryForeground: FEATURE_PRIMARY_FOREGROUND,
      primaryBorder: chatScale.border,
      primaryMuted: chatScale.muted,
    },
    borders: {
      ...resolvedSemantic.borders,
      focusRing: hexToRgba(chatScale.main, 0.22),
    },
  };

  return { featurePalette, featureSemantic };
}

/**
 * Feature-scoped theme — tokens, palette, and form control overrides.
 * Use inside FeatureThemeProvider on feature pages (Intervene, License, Onboarding).
 */
export function buildFeatureTheme(themeConfig = {}, systemMode = 'light') {
  const {
    requestedMode,
    customPalette,
    chatScale,
    resolvedSemantic,
    densityResolved,
  } = resolveThemeContext(themeConfig, systemMode);

  const { featurePalette, featureSemantic } = applyFeatureChatAccent({
    customPalette,
    chatScale,
    resolvedSemantic,
  });

  return createTheme({
    palette: {
      ...featurePalette,
      mode: requestedMode,
      chat: featureSemantic.chat,
      status: featureSemantic.status,
    },
    spacing: spacingFunction,
    typography: {
      fontFamily: typography.fontFamilyBase,
      button: {
        textTransform: 'none',
        fontWeight: typography.fontWeightMedium,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            textTransform: 'none',
          },
          containedPrimary: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: shadows.sm,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: getFormControlThemeOverrides({
          resolvedSemantic: featureSemantic,
          controlHeights: densityResolved.controlHeights,
          inputSize: 'sm',
        }),
      },
      MuiFormHelperText: {
        styleOverrides: getFormHelperTextThemeOverrides(featureSemantic),
      },
      MuiInputLabel: {
        styleOverrides: getFormLabelThemeOverrides(featureSemantic),
      },
    },
    customTokens: buildCustomTokens({
      densityResolved,
      resolvedSemantic: featureSemantic,
      requestedMode,
    }),
  });
}

/**
 * Full application theme — includes global MUI overrides for eventual app-wide rollout.
 */
export function buildDynamicTheme(themeConfig = {}, systemMode = 'light') {
  const {
    requestedMode,
    customPalette,
    resolvedSemantic,
    densityResolved,
  } = resolveThemeContext(themeConfig, systemMode);

  const customTokens = buildCustomTokens({ densityResolved, resolvedSemantic, requestedMode });

  return createTheme({
    palette: {
      ...customPalette,
      mode: requestedMode,
      chat: resolvedSemantic.chat,
      status: resolvedSemantic.status,
    },
    spacing: spacingFunction,
    shape: {
      borderRadius: radius.lg,
    },
    customTokens,
    typography: {
      fontFamily: typography.fontFamilyBase,
      fontSize: 15,
      h1: {
        fontSize: typography.fontSize4xl,
        fontWeight: typography.fontWeightSemibold,
        lineHeight: typography.lineHeightHeading,
      },
      h2: {
        fontSize: typography.fontSize3xl,
        fontWeight: typography.fontWeightSemibold,
        lineHeight: typography.lineHeightHeading,
      },
      h3: {
        fontSize: typography.fontSize2xl,
        fontWeight: typography.fontWeightSemibold,
        lineHeight: typography.lineHeightHeading,
      },
      body1: {
        fontSize: typography.fontSizeMd,
        lineHeight: typography.lineHeightBody,
      },
      body2: {
        fontSize: typography.fontSizeSm,
        lineHeight: typography.lineHeightBody,
      },
      caption: {
        fontSize: typography.fontSizeXs,
        lineHeight: typography.lineHeightBody,
      },
      button: {
        textTransform: 'none',
        fontWeight: typography.fontWeightMedium,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: requestedMode,
          },
          body: {
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family-base)',
            fontSize: 'var(--font-size-md)',
            lineHeight: 'var(--line-height-body)',
            fontWeight: 'var(--font-weight-regular)',
          },
          a: {
            color: 'var(--color-link)',
            textDecoration: 'none',
          },
          'a:hover': {
            color: 'var(--color-link-hover)',
            textDecoration: 'underline',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: densityResolved.controlHeights.md,
            padding: '0 16px',
            borderRadius: radius.lg,
            fontSize: typography.fontSizeSm,
            fontWeight: typography.fontWeightMedium,
            boxShadow: 'none',
            transition:
              'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, transform 120ms ease',
            '&:focus-visible': {
              boxShadow: `0 0 0 3px ${resolvedSemantic.borders.focusRing}`,
            },
            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: densityResolved.controlHeights.lg,
            borderRadius: resolvedSemantic.inputs?.radius || radiusPx.md,
            backgroundColor: resolvedSemantic.inputs?.background || resolvedSemantic.surfaces.surface,
            transition: 'border-color 160ms ease, background-color 160ms ease',
            '& .MuiOutlinedInput-notchedOutline': {
              top: 0,
              borderColor: resolvedSemantic.inputs?.border || resolvedSemantic.borders.border,
              borderWidth: '1px !important',
            },
            '&:hover': {
              backgroundColor:
                resolvedSemantic.inputs?.backgroundHover ||
                resolvedSemantic.inputs?.background ||
                resolvedSemantic.surfaces.surface,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: resolvedSemantic.borders.borderStrong,
              borderWidth: '1px !important',
            },
            '&.Mui-focused': {
              backgroundColor:
                resolvedSemantic.inputs?.backgroundFocus || resolvedSemantic.surfaces.surface,
              boxShadow: 'none',
              outline: 'none',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: resolvedSemantic.interactive.primary,
              borderWidth: '1px !important',
            },
            '&.Mui-disabled': {
              backgroundColor:
                resolvedSemantic.inputs?.backgroundDisabled ||
                resolvedSemantic.inputs?.background ||
                resolvedSemantic.surfaces.surface,
              cursor: resolvedSemantic.inputs?.cursorDisabled || 'not-allowed',
            },
          },
          notchedOutline: {
            top: 0,
            borderWidth: '1px !important',
          },
          input: {
            fontSize: resolvedSemantic.inputs?.fontSize || typography.fontSizeMd,
            paddingTop: 10,
            paddingBottom: 10,
            '&.Mui-disabled': {
              cursor: resolvedSemantic.inputs?.cursorDisabled || 'not-allowed',
              WebkitTextFillColor: 'inherit',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: typography.fontWeightMedium,
          },
          labelSmall: {
            fontSize: '0.6875rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 34,
            paddingLeft: 12,
            paddingRight: 12,
            borderRadius: radius.md,
            fontSize: typography.fontSizeSm,
            fontWeight: typography.fontWeightMedium,
            textTransform: 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: radius.xl,
            border: `1px solid ${resolvedSemantic.borders.border}`,
            boxShadow: shadows.xl,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius.lg,
            border: `1px solid ${resolvedSemantic.borders.border}`,
            boxShadow: shadows.md,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: resolvedSemantic.interactive.primary,
            textDecoration: 'none',
            '&:hover': {
              color: resolvedSemantic.interactive.primaryHover,
              textDecoration: 'underline',
            },
          },
        },
      },
    },
  });
}
